import { TrackedObject } from 'tracked-built-ins';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import AwayMessageEditHandler from '../components/messaging-routes/away-message-edit-handler.js';
import AwayMessageEditPage from '../components/messaging-routes/away-message-edit.gjs';
import ContactsHandler from '../components/messaging-routes/contacts-handler.js';
import ContactsPage from '../components/messaging-routes/contacts.gjs';
import ConversationDetailsHandler from '../components/messaging-routes/conversation-details-handler.js';
import ConversationDetailsPage from '../components/messaging-routes/conversation-details.gjs';
import ConversationHandler from '../components/messaging-routes/conversation-handler.js';
import ConversationListHandler from '../components/messaging-routes/conversation-list-handler.js';
import ConversationListPage from '../components/messaging-routes/conversation-list.gjs';
import ConversationPage from '../components/messaging-routes/conversation.gjs';
import Service from '@ember/service';

/**
 * Route map defining all available messaging routes with their associated
 * components and handlers. Each map key must match that route's `name` so
 * serialized history can resolve via ROUTE_MAP[routeName].
 * @type {Object}
 */
export const ROUTE_MAP = {
  awayMessageEdit: {
    name: 'awayMessageEdit',
    component: AwayMessageEditPage,
    handler: AwayMessageEditHandler,
  },
  contacts: {
    name: 'contacts',
    component: ContactsPage,
    handler: ContactsHandler,
  },
  conversation: {
    name: 'conversation',
    component: ConversationPage,
    handler: ConversationHandler,
  },
  conversationList: {
    name: 'conversationList',
    component: ConversationListPage,
    handler: ConversationListHandler,
  },
  conversationDetails: {
    name: 'conversationDetails',
    component: ConversationDetailsPage,
    handler: ConversationDetailsHandler,
    parent: 'conversation',
  },
};

/** @type {number} */
export const MAX_ROUTE_HISTORY = 10;

/**
 * Manages navigation between different routes within the messaging client.
 * This service handles route state management and coordinates the lifecycle
 * of route handlers (onVisit/onLeave hooks) when navigating between routes.
 * @class MessagingNavigationService
 * @extends Service
 */
export default class MessagingNavigationService extends Service {
  /**
   * Stack of active routes. The top of the stack is the current route.
   * Child routes (e.g. conversation details) are pushed so the
   * parent handler (e.g. conversation with messages) is preserved and not refetched on back.
   * @type {Object[]}
   */
  @tracked routeStack = [];

  /**
   * An array of route stacks which represent the history of navigations.
   * Used by {@link goToPreviousRoute} to restore previous route stacks.
   * @type {Array<{ routeName: string, options: Object }[]>}
   */
  @tracked historyStack = [];

  /**
   * The currently active route (top of the stack).
   * @type {Object|null}
   * @property {Component} component - The component to render for this route
   * @property {Object} handler - The route handler instance with onVisit/onLeave hooks
   * @property {string} name - The name of the route
   */
  get currentRoute() {
    return this.routeStack[this.routeStack.length - 1] ?? null;
  }

  constructor() {
    super(...arguments);
    this.#navigateTo(ROUTE_MAP.conversationList, {}, { recordHistory: false });
  }

  /**
   * Checks if the provided route(s) match the current route.
   * @param {Object|Object[]} routes - A single route object or array of route objects to check
   * @returns {boolean} True if any of the provided routes match the current route
   */
  isCurrentRoute(routes) {
    if (!this.currentRoute) return false;
    if (Array.isArray(routes)) {
      return routes.some(route => route.name === this.currentRoute.name);
    }
    return routes.name === this.currentRoute.name;
  }

  async toAwayMessageEdit() {
    await this.#navigateTo(ROUTE_MAP.awayMessageEdit);
  }

  async toContacts() {
    await this.#navigateTo(ROUTE_MAP.contacts);
  }

  async toConversation(conversation) {
    await this.#navigateTo(ROUTE_MAP.conversation, { conversation });
  }

  async toConversationList() {
    await this.#navigateTo(ROUTE_MAP.conversationList);
  }

  async toConversationDetails() {
    await this.#navigateTo(ROUTE_MAP.conversationDetails);
  }

  async goToPreviousRoute() {
    // If no history, fallback to conversation list.
    const previous = this.historyStack[this.historyStack.length - 1];
    if (!previous) {
      await this.toConversationList();
      return;
    }

    // Otherwise, restore the previous route stack.
    this.historyStack = this.historyStack.slice(0, -1);
    await this.#restoreSerializedStack(previous);
  }

  /**
   * Leaves the current child route and shows the route under it (no refetch).
   * Only has an effect when the route stack has more than one entry; otherwise returns false.
   * @returns {Promise<boolean>} True when a child was left, false when stack had only one entry
   */
  async leaveChildRoute() {
    if (this.routeStack.length <= 1) {
      return false;
    }
    const current = this.currentRoute;
    await current.handler.onLeave.perform();
    this.routeStack = this.routeStack.slice(0, -1);
    return true;
  }

  /**
   * Internal method that handles navigation between routes.
   * When the target route has a parent and the current route is that parent,
   * pushes the child onto the stack (preserving parent handler and loaded data).
   * Otherwise replaces the stack with the new route.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} [options={}] - Options to pass to the route handler constructor
   * @param {Object} [navMeta={}] - Navigation metadata
   * @param {boolean} [navMeta.recordHistory=true] - When false, the current stack is not pushed onto historyStack before replace
   * @returns {Promise<void>} Resolves when navigation is complete
   */
  async #navigateTo(route, options = {}, { recordHistory = true } = {}) {
    const shouldPushChild = route.parent && this.currentRoute?.name === route.parent;

    if (shouldPushChild) {
      const childOptions = { ...options, parentHandler: this.currentRoute.handler };
      const childRoute = this.#buildCurrentRoute(route, childOptions);
      this.routeStack = [...this.routeStack, childRoute];
      await childRoute.handler.onVisit.perform();
      return;
    }

    if (recordHistory && this.routeStack.length) {
      this.#pushHistoryEntry(this.#serializeRouteStackForHistory());
    }

    for (const routeEntry of [...this.routeStack].reverse()) {
      await routeEntry.handler.onLeave.perform();
    }
    this.routeStack = [this.#buildCurrentRoute(route, options)];
    await this.currentRoute.handler.onVisit.perform();
  }

  /**
   * Builds a tracked route object with the component, handler instance, and route name.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} options - Options to pass to the route handler constructor
   * @returns {TrackedObject} The constructed route object
   */
  #buildCurrentRoute(route, options) {
    return new TrackedObject({
      component: route.component,
      // eslint-disable-next-line new-cap
      handler: new route.handler(getOwner(this), options),
      name: route.name,
    });
  }

  /**
   * @param {{ routeName: string, options: Object }[]} historyEntry
   */
  #pushHistoryEntry(historyEntry) {
    const updatedStack = [...this.historyStack, historyEntry];
    while (updatedStack.length > MAX_ROUTE_HISTORY) {
      // Once we exceed the max history length, kick out the oldest entries.
      updatedStack.shift();
    }
    this.historyStack = updatedStack;
  }

  #serializeRouteStackForHistory() {
    return this.routeStack.map(route => ({
      routeName: route.name,
      options: this.#cloneOptionsForHistory(route.handler.options),
    }));
  }

  #cloneOptionsForHistory(options) {
    if (!options || typeof options !== 'object') {
      return {};
    }
    const clone = { ...options };
    // Since we rebuild the parent handler on restore, we don't want to include it in the history.
    delete clone.parentHandler;
    return clone;
  }

  /**
   * Replaces the stack with the conversation list.
   * This is used as a fallback when the history is corrupt or inconsistent.
   * @private
   */
  async #fallbackToConversationList() {
    this.routeStack = [this.#buildCurrentRoute(ROUTE_MAP.conversationList, {})];
    await this.currentRoute.handler.onVisit.perform();
  }

  /**
   * @param {{ routeName: string, options: Object }[]} serializedStack
   */
  async #restoreSerializedStack(serializedStack) {
    // Leave all current routes.
    for (const routeEntry of [...this.routeStack].reverse()) {
      await routeEntry.handler.onLeave.perform();
    }

    // Restore the previous route stack.
    const restoredStack = [];
    for (const serializedRoute of serializedStack) {
      const routeDef = ROUTE_MAP[serializedRoute.routeName];
      if (!routeDef) {
        return this.#fallbackToConversationList();
      }
      const options = { ...this.#cloneOptionsForHistory(serializedRoute.options) };
      if (routeDef.parent) {
        const parent = restoredStack[restoredStack.length - 1];
        if (!parent || parent.name !== routeDef.parent) {
          return this.#fallbackToConversationList();
        }
        options.parentHandler = parent.handler;
      }
      restoredStack.push(this.#buildCurrentRoute(routeDef, options));
    }

    // Set the restored stack and visit each route.
    this.routeStack = restoredStack;
    for (const entry of restoredStack) {
      await entry.handler.onVisit.perform();
    }
  }
}
